import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Monitor, Smartphone, Tablet, Clock, Globe, Eye } from 'lucide-react';

const VisitorDetails = ({ visitors, onSelectSession }) => {
  const getDeviceIcon = (deviceType) => {
    switch(deviceType?.toLowerCase()) {
      case 'mobile': return <Smartphone className="h-4 w-4 text-purple-600" />;
      case 'tablet': return <Tablet className="h-4 w-4 text-green-600" />;
      default: return <Monitor className="h-4 w-4 text-blue-600" />;
    }
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  const getLocationDisplay = (visitor) => {
    // Priority: City > State > Country > ISP > "Direct Visitor"
    if (visitor.city && visitor.city !== 'Unknown') {
      return `${visitor.city}, ${visitor.state || visitor.country || ''}`;
    }
    if (visitor.state && visitor.state !== 'Unknown') {
      return `${visitor.state}, ${visitor.country || 'India'}`;
    }
    if (visitor.country && visitor.country !== 'Unknown') {
      return visitor.country;
    }
    if (visitor.isp && visitor.isp !== 'Unknown') {
      return visitor.isp;
    }
    // Show referrer source if available
    if (visitor.referrer && visitor.referrer !== 'Direct' && visitor.referrer !== 'Internal') {
      return `via ${visitor.referrer}`;
    }
    return 'Direct Visitor';
  };

  // Sort visitors by most recent first (newest at top)
  const sortedVisitors = [...visitors].sort((a, b) => {
    // Try to sort by lastSeen first
    if (a.lastSeen && b.lastSeen) {
      return new Date(b.lastSeen) - new Date(a.lastSeen);
    }
    // Fall back to startTime if lastSeen not available
    if (a.startTime && b.startTime) {
      return new Date(b.startTime) - new Date(a.startTime);
    }
    // If no timestamps, keep original order
    return 0;
  });

  // Check if visitor is new (arrived in last 30 seconds)
  const isNewVisitor = (visitor) => {
    const now = new Date();
    const startTime = new Date(visitor.startTime);
    const diffInSeconds = (now - startTime) / 1000;
    return diffInSeconds <= 30;
  };

  return (
    <Card className="h-[500px] shadow-lg">
      <CardContent className="p-3 h-full flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <Eye className="h-4 w-4 text-emerald-600" />
            Live Visitors
          </h3>
          <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-semibold">
            {visitors.length}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {sortedVisitors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Globe className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-xs">No live visitors</p>
            </div>
          ) : (
            sortedVisitors.map((visitor) => (
              <Card
                key={visitor.sessionId}
                className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-emerald-500"
                onClick={() => onSelectSession && onSelectSession(visitor.sessionId)}
              >
                <CardContent className="p-2">
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-1">
                      {getDeviceIcon(visitor.deviceType)}
                      <span className="text-xs font-semibold text-gray-900 truncate">
                        {getLocationDisplay(visitor)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {isNewVisitor(visitor) && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-bold animate-pulse">
                          NEW
                        </span>
                      )}
                      <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                        Live
                      </span>
                    </div>
                  </div>

                  <div className="space-y-0.5">
                    <p className="text-xs text-gray-600 truncate">
                      {visitor.deviceType} - {visitor.browser}
                    </p>
                    {visitor.ipAddress && visitor.ipAddress !== 'Unknown' && (
                      <p className="text-xs text-gray-500 truncate">
                        {visitor.ipAddress}
                      </p>
                    )}
                    <p className="text-xs text-emerald-600 font-semibold">
                      {formatDuration(visitor.duration || 0)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default VisitorDetails;